import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const NotificationContext = createContext(undefined);
const STORAGE_KEY = 'rsdashboard.notifications';
const MAX_NOTIFICATIONS = 50;
const POLL_INTERVAL_MS = 60000;

const safeParseNotifications = (rawValue) => {
  if (!rawValue) return [];
  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .filter((item) => item && typeof item === 'object' && item.message)
      .map((item) => ({
        id: item.id,
        type: item.type || 'info',
        message: item.message,
        payload: item.payload || null,
        is_read: Boolean(item.is_read),
        created_at: item.created_at || null
      }));
  } catch (error) {
    console.error('Unable to parse stored notifications:', error);
    return [];
  }
};

const getStoredUserEmail = () => {
  try {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed?.email || null;
  } catch (error) {
    console.error('Unable to parse stored user:', error);
    return null;
  }
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState(() =>
    safeParseNotifications(localStorage.getItem(STORAGE_KEY))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollHandleRef = useRef(null);

  const persistNotifications = useCallback((entries) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (storageError) {
      console.error('Unable to persist notifications:', storageError);
    }
  }, []);

  const normalizeNotifications = useCallback((items = []) => {
    return items
      .filter((item) => item && item.message)
      .map((item) => ({
        id: item.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        type: item.type || 'info',
        message: item.message,
        payload: item.payload || null,
        is_read: Boolean(item.is_read),
        created_at: item.created_at || null
      }))
      .slice(0, MAX_NOTIFICATIONS);
  }, []);

  const fetchNotifications = useCallback(async () => {
    const email = getStoredUserEmail();
    if (!email) {
      setNotifications([]);
      persistNotifications([]);
      return [];
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/notifications/`, {
        params: {
          email,
          include_read: 'false',
          limit: MAX_NOTIFICATIONS
        },
        withCredentials: true
      });

      const normalized = normalizeNotifications(response.data?.notifications || []);
      setNotifications(normalized);
      persistNotifications(normalized);
      setError(null);
      return normalized;
    } catch (fetchError) {
      console.error('Unable to load notifications:', fetchError);
      setError(fetchError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [normalizeNotifications, persistNotifications]);

  const refreshNotifications = useCallback(() => fetchNotifications(), [fetchNotifications]);

  const addNotification = useCallback(
    ({ message, type = 'info', payload = null, created_at = new Date().toISOString() }) => {
      if (!message) {
        return;
      }
      setNotifications((prev) => {
        const next = [
          {
            id: `${created_at}-${Math.random().toString(36).slice(2, 8)}`,
            message,
            type,
            payload,
            is_read: false,
            created_at
          },
          ...prev
        ].slice(0, MAX_NOTIFICATIONS);
        persistNotifications(next);
        return next;
      });
    },
    [persistNotifications]
  );

  const markNotificationRead = useCallback(
    async (notificationId) => {
      if (!notificationId) return;
      const email = getStoredUserEmail();
      if (!email) return;

      try {
        await axios.post(
          `${API_BASE_URL}/notifications/mark-read`,
          {
            email,
            notification_ids: [notificationId]
          },
          { withCredentials: true }
        );
      } catch (requestError) {
        console.error('Unable to mark notification as read:', requestError);
      }

      setNotifications((prev) => {
        const next = prev.filter((notification) => notification.id !== notificationId);
        persistNotifications(next);
        return next;
      });
    },
    [persistNotifications]
  );

  const markAllAsRead = useCallback(async () => {
    if (!notifications.length) {
      persistNotifications([]);
      setNotifications([]);
      return;
    }

    const email = getStoredUserEmail();
    if (!email) {
      persistNotifications([]);
      setNotifications([]);
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/notifications/mark-read`,
        {
          email,
          mark_all: true
        },
        { withCredentials: true }
      );
    } catch (requestError) {
      console.error('Unable to mark notifications as read:', requestError);
    }

    setNotifications([]);
    persistNotifications([]);
  }, [notifications.length, persistNotifications]);

  const removeNotification = useCallback(
    (notificationId) => {
      if (!notificationId) return;
      markNotificationRead(notificationId);
    },
    [markNotificationRead]
  );

  const clearNotifications = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  useEffect(() => {
    refreshNotifications();
    pollHandleRef.current = window.setInterval(
      refreshNotifications,
      POLL_INTERVAL_MS
    );
    return () => {
      if (pollHandleRef.current) {
        window.clearInterval(pollHandleRef.current);
      }
    };
  }, [refreshNotifications]);

  const contextValue = useMemo(
    () => ({
      notifications,
      loading,
      error,
      unreadCount: notifications.length,
      refreshNotifications,
      addNotification,
      markNotificationRead,
      markAllAsRead,
      removeNotification,
      clearNotifications
    }),
    [
      notifications,
      loading,
      error,
      refreshNotifications,
      addNotification,
      markNotificationRead,
      markAllAsRead,
      removeNotification,
      clearNotifications
    ]
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
